import { MapUtils } from '../../../../../utility/map-utils.js';
import { cloneData, isSameData } from '../../../../dto/data-model-utils.js';
import {
  SerializedData, SerializationContext, DataModelBuilder,
} from '../../../../dto/data-model.js';
import { GenericOrder } from '../../../orders/generic-order.js';
import { AuditLogEntryType } from '../../audit-log-entry-type.js';
import { AuditLogEntry } from '../../audit-log-entry.js';

export class GenericSeatedEventTicketSwapEntry extends AuditLogEntry {
  static readonly TYPE_ID: string = '881a3737f1bb4647b944f2ed16dd2cfe';

  private constructor(
    public readonly ticketId: string,
    public readonly orderEntryId: string,
    public readonly oldSeatId: number,
    public readonly newSeatId: number,
    public readonly before: GenericOrder,
    public readonly after: GenericOrder,
  ) {
    super();
  }

  get type(): AuditLogEntryType {
    return AuditLogEntryType.GenericSeatedEventTicketSwap;
  }

  generateQueryData(): SerializedData {
    const orderEntry = this.after.findEntry(this.orderEntryId);
    const eventId = orderEntry?.tryGetEventId();

    return {
      businessRef: this.after.businessRef.encode(),
      eventId: eventId ?? null,
      orderId: this.after.uniqueId,
      ticketId: this.ticketId,
      orderEntryId: this.orderEntryId,
    };
  }

  get $typeId(): string {
    return GenericSeatedEventTicketSwapEntry.TYPE_ID;
  }

  static create(args: {
    ticketId: string,
    orderEntryId: string,
    oldSeatId: number,
    newSeatId: number,
    before: GenericOrder,
    after: GenericOrder,
  }): GenericSeatedEventTicketSwapEntry {
    return new GenericSeatedEventTicketSwapEntry(
        args.ticketId,
        args.orderEntryId,
        args.oldSeatId,
        args.newSeatId,
        args.before,
        args.after,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): GenericSeatedEventTicketSwapEntry {
    return this.create({
      ticketId: MapUtils.getString(map, 'ticketId'),
      orderEntryId: MapUtils.getString(map, 'orderEntryId'),
      oldSeatId: MapUtils.getNumber(map, 'oldSeatId'),
      newSeatId: MapUtils.getNumber(map, 'newSeatId'),
      before: context.fromData<GenericOrder>(MapUtils.getRecord(map, 'before')),
      after: context.fromData<GenericOrder>(MapUtils.getRecord(map, 'after')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      ticketId: this.ticketId,
      orderEntryId: this.orderEntryId,
      oldSeatId: this.oldSeatId,
      newSeatId: this.newSeatId,
      before: context.toData(this.before),
      after: context.toData(this.after),
    };
  }

  clone(): this {
    return GenericSeatedEventTicketSwapEntry.create({
      ticketId: this.ticketId,
      orderEntryId: this.orderEntryId,
      oldSeatId: this.oldSeatId,
      newSeatId: this.newSeatId,
      before: cloneData(this.before),
      after: cloneData(this.after),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.ticketId === this.ticketId &&
          other.orderEntryId === this.orderEntryId &&
          other.oldSeatId === this.oldSeatId &&
          other.newSeatId === this.newSeatId &&
          isSameData(other.before, this.before) &&
          isSameData(other.after, this.after);
  }
}

export class GenericSeatedEventTicketSwapEntryBuilder extends DataModelBuilder<GenericSeatedEventTicketSwapEntry> {
  build(context: SerializationContext, map: SerializedData): GenericSeatedEventTicketSwapEntry {
    return GenericSeatedEventTicketSwapEntry.fromMap(context, map);
  }
}
